import { Component, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription, TimeoutError, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map } from 'rxjs/operators';

import {
  ImportZiel,
  ProjektListenTyp,
  SpsAnlagenteilSpalte,
  SpsAnlagenteilWert,
  SpsImportErgebnis,
  SpsImportVorschauZeile,
  anlagenteilSpalteText,
  istSpsListenTyp,
  listenTypInfo
} from '../../../../../Models/Sps';
import { ListenTyp, MessstellenListeDetail } from '../../../../../Models/messstelle.model';
import {
  ImportDatei,
  ImportDateiErgebnis,
  ImportDateiStatus,
  dateiSchluesselVon,
  neueImportDatei
} from '../../../../../Models/listen-import.model';
import { VorbereiteterKiImport } from '../../../../../Models/vorbereiteter-ki-import.model';
import { ListenImportKoordinatorService } from '../../../../../Services/listen-import-koordinator.service';
import { SpsImportApiService } from '../../../../../Services/sps-import-api.service';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';
import { ListenImportEntwurfService } from '../../../../../Services/listen-import-entwurf.service';
import { InbetriebnahmeService } from '../../../../../Services/inbetriebnahme.service';
import { MessstellenImportComponent } from '../../messstellen-import/messstellen-import.component';

/**
 * Gemeinsamer Importassistent für alle vier Listenarten eines Projekts.
 *
 *  Schritt 1  Dateien wählen, Typ erkennen lassen oder korrigieren – nichts wird gespeichert.
 *  Schritt 2  Je Datei eine Vorschau: SPS-Listen über die strukturierte Backend-Vorschau
 *             (seitenweise), Messstellen-/Verbraucherlisten über die eingebettete
 *             KI-Komponente. Jede KI-Komponente bleibt nach dem Erzeugen erhalten
 *             (nur verborgen) – Einstellungen und Analyse überleben den Dateiwechsel.
 *  Schritt 3  Zusammenfassung, dann „Import starten“: nacheinander je Datei der
 *             jeweils bestehende Import (InbetriebnahmeService.import bzw.
 *             SpsImportApiService.importieren). Der Gesamtimport ist NICHT atomar;
 *             jede Datei trägt ihren eigenen Status. Erfolgreiche Dateien werden nie
 *             erneut gesendet, unklare (keine Antwort) erst nach Prüfung durch den Benutzer.
 */
@Component({
  selector: 'app-sps-mehrfach-import',
  templateUrl: './sps-mehrfach-import.component.html',
  styleUrls: ['./sps-mehrfach-import.component.css']
})
export class SpsMehrfachImportComponent implements OnInit, OnDestroy {

  projektId = 0;
  projektName = '';

  /** Vorbelegter Typ aus dem Listentyp-Dialog (Query-Parameter `listenTyp`). */
  vorgewaehlterTyp: ProjektListenTyp | null = null;

  dateien: ImportDatei[] = [];
  aktuelleDateiIndex = 0;
  schritt: 1 | 2 | 3 = 1;

  /** Hinweis oberhalb der Aktionen (Validierung, Importfehler). */
  fehler = '';

  importLaeuft = false;
  /** Welche Datei gerade gesendet wird – für die Anzeige in Schritt 3. */
  importAktuelleDateiId: string | null = null;

  readonly listenTypen: ProjektListenTyp[] = [
    'Messstellenliste', 'Verbraucherliste', 'SpsZuordnungsliste', 'PlcTagListe'
  ];

  readonly vorschauSeitengroessen = [25, 50, 100];

  /**
   * Quelle der Anlagenteile für diesen Import: 'Ort' (SPS-Zuordnungsliste) oder
   * 'Path' (PLC-Tag-Liste), null = keine. Gilt für alle SPS-Dateien des gewählten Typs;
   * angeboten werden nur Quellen, für die eine passende Datei mit dieser Spalte vorliegt.
   */
  anlagenteilQuelle: SpsAnlagenteilSpalte | null = null;

  /** Alle eingebetteten KI-Komponenten – je KI-Datei genau eine, dauerhaft erhalten. */
  @ViewChildren(MessstellenImportComponent)
  kiKomponenten!: QueryList<MessstellenImportComponent>;

  private laufendeAnfragen = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private koordinator: ListenImportKoordinatorService,
    private spsImportApi: SpsImportApiService,
    private ibnService: InbetriebnahmeService,
    private entwurf: ListenImportEntwurfService
  ) { }

  ngOnInit(): void {
    this.projektId = Number(this.route.snapshot.paramMap.get('projektId')) || 0;
    this.projektName = this.route.snapshot.queryParamMap.get('projektName') ?? '';

    const typ = this.route.snapshot.queryParamMap.get('listenTyp') as ProjektListenTyp | null;
    this.vorgewaehlterTyp = typ && typ !== 'Unbekannt' && listenTypInfo(typ).typ === typ ? typ : null;

    this.entwurfWiederherstellen();
  }

  ngOnDestroy(): void {
    // Laufende Erkennungen/Vorschauen gehören zu dieser Instanz; der Entwurf
    // bleibt im Service, läuft aber nicht mehr weiter.
    this.laufendeAnfragen.unsubscribe();

    for (const d of this.dateien) {
      d.erkennungLaeuft = false;
      d.vorschauLaeuft = false;
    }

    this.entwurfSpeichern();
  }


  // =========================================================
  // SCHRITT 1 – DATEIEN WÄHLEN UND ERKENNEN (speichert nichts)
  // =========================================================

  dateienGewaehlt(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';

    this.dateienVerarbeiten(files);
  }

  private dateienVerarbeiten(files: File[]): void {
    const doppelt: string[] = [];
    const ungueltig: string[] = [];

    for (const file of files) {
      if (!/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
        ungueltig.push(file.name);
        continue;
      }

      if (this.dateien.some(d => d.dateiSchluessel === dateiSchluesselVon(file))) {
        doppelt.push(file.name);
        continue;
      }

      this.dateiHinzufuegen(file);
    }

    if (doppelt.length > 0) {
      this.snackBar.open(
        `Bereits ausgewählt und übersprungen: ${doppelt.join(', ')}`,
        'OK',
        { duration: 5000 }
      );
    }

    if (ungueltig.length > 0) {
      this.snackBar.open(
        `Nicht unterstützte Dateien: ${ungueltig.join(', ')}`,
        'OK',
        { duration: 5000 }
      );
    }
  }


  // =========================================================
  // DRAG & DROP
  //
  // Seitenweit statt nur auf der kleinen Zone: Ohne preventDefault auf
  // `dragover`/`drop` am Dokument öffnet der Browser eine daneben
  // losgelassene Datei selbst – der Import wirkt dann „kaputt". Außerdem
  // liefert der Browser bei Dateien aus dem Explorer kein `relatedTarget`,
  // sodass ein Wechsel auf ein Kind-Element wie ein Verlassen aussieht;
  // deshalb zählt ein Zähler die offenen `dragenter` gegen die `dragleave`.
  // =========================================================

  /** Eine Datei schwebt über der Seite – Zone und Overlay heben sich hervor. */
  isDragging = false;

  /** Offene dragenter minus dragleave; 0 heißt „wirklich draußen". */
  private dragTiefe = 0;

  /** Dürfen Dateien gerade angenommen werden? Nur in Schritt 1 und nicht während des Imports. */
  get dropErlaubt(): boolean {
    return this.schritt === 1 && !this.importLaeuft;
  }

  @HostListener('document:dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    if (!SpsMehrfachImportComponent.traegtDateien(event)) {
      return;
    }

    event.preventDefault();
    this.dragTiefe++;
    this.isDragging = this.dropErlaubt;
  }

  @HostListener('document:dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (!SpsMehrfachImportComponent.traegtDateien(event)) {
      return;
    }

    // Pflicht: ohne preventDefault feuert `drop` nicht, und der Browser
    // würde die Datei stattdessen öffnen.
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.dropErlaubt ? 'copy' : 'none';
    }

    this.isDragging = this.dropErlaubt;
  }

  @HostListener('document:dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (!SpsMehrfachImportComponent.traegtDateien(event)) {
      return;
    }

    event.preventDefault();
    this.dragTiefe = Math.max(0, this.dragTiefe - 1);

    if (this.dragTiefe === 0) {
      this.isDragging = false;
    }
  }

  @HostListener('document:drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (!SpsMehrfachImportComponent.traegtDateien(event)) {
      return;
    }

    // Immer abfangen – auch wenn gerade nichts angenommen wird, darf der
    // Browser die Datei nicht öffnen.
    event.preventDefault();
    this.dragZuruecksetzen();

    if (!this.dropErlaubt) {
      this.snackBar.open(
        this.importLaeuft
          ? 'Während des Imports können keine Dateien hinzugefügt werden.'
          : 'Dateien lassen sich nur in Schritt 1 („Dateien auswählen") hinzufügen.',
        'OK',
        { duration: 4000 }
      );
      return;
    }

    const files = SpsMehrfachImportComponent.dateienAus(event.dataTransfer);

    if (files.length === 0) {
      this.snackBar.open('Es wurden keine Dateien erkannt – bitte Excel-Dateien ablegen.', 'OK', { duration: 4000 });
      return;
    }

    this.dateienVerarbeiten(files);
  }

  /** Das Fenster verliert den Fokus oder der Zug endet außerhalb: nichts bleibt hervorgehoben. */
  @HostListener('window:blur')
  @HostListener('document:dragend')
  dragZuruecksetzen(): void {
    this.dragTiefe = 0;
    this.isDragging = false;
  }

  /** Nur Datei-Züge sind gemeint – kein Text, kein Link, kein cdk-Drag innerhalb der Seite. */
  private static traegtDateien(event: DragEvent): boolean {
    const typen = event.dataTransfer?.types;

    if (!typen) {
      return false;
    }

    return Array.from(typen).includes('Files');
  }

  /** Dateien aus dem Drop lesen – über `files`, sonst über die einzelnen Einträge. */
  private static dateienAus(dataTransfer: DataTransfer | null): File[] {
    if (!dataTransfer) {
      return [];
    }

    if (dataTransfer.files && dataTransfer.files.length > 0) {
      return Array.from(dataTransfer.files);
    }

    const files: File[] = [];

    for (const item of Array.from(dataTransfer.items ?? [])) {
      if (item.kind === 'file') {
        const file = item.getAsFile();

        if (file) {
          files.push(file);
        }
      }
    }

    return files;
  }

  dateiHinzufuegen(file: File): void {
    const datei = neueImportDatei(file, this.vorgewaehlterTyp ?? 'Unbekannt', this.vorgewaehlterTyp != null);

    this.dateien = [...this.dateien, datei];
    this.fehler = '';
    this.entwurfSpeichern();
    this.erkennungStarten(datei);
  }

  erkennungStarten(datei: ImportDatei): void {
    datei.erkennungLaeuft = true;
    datei.fehler = null;

    const sub = this.koordinator.erkenneDatei(datei.file)
      .pipe(finalize(() => {
        datei.erkennungLaeuft = false;
        this.entwurfSpeichern();
      }))
      .subscribe({
        next: erkennung => {
          // Die Datei kann inzwischen entfernt worden sein.
          if (!this.dateien.includes(datei)) {
            return;
          }

          datei.erkennung = erkennung;

          // Erkennung schlägt vor – eine Wahl des Benutzers bleibt unangetastet.
          if (!datei.typManuellGesetzt && erkennung.typ !== 'Unbekannt') {
            datei.listenTyp = erkennung.typ;
          }

          if (istSpsListenTyp(datei.listenTyp)) {
            datei.sheetName = erkennung.blatt;
          }
        },
        error: err => {
          datei.fehler = SpsPruefungApiService.fehlertext(err, 'Die Datei konnte nicht gelesen werden.');
        }
      });

    this.laufendeAnfragen.add(sub);
  }

  entfernen(datei: ImportDatei): void {
    if (this.importLaeuft) {
      return;
    }

    this.dateien = this.dateien.filter(d => d !== datei);
    this.aktuelleDateiIndex = Math.min(this.aktuelleDateiIndex, Math.max(0, this.dateien.length - 1));
    this.fehler = '';

    if (this.dateien.length === 0 && this.schritt !== 1) {
      this.schritt = 1;
    }

    this.entwurfSpeichern();
  }

  typAuswahlGeaendert(datei: ImportDatei, event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.typSetzen(datei, select.value as ProjektListenTyp);
  }

  /**
   * Manuelle Typkorrektur. Alles, was vom alten Typ abhing (SPS-Vorschau,
   * KI-Vorbereitung, Bestätigung), wird ungültig. Ein Wechsel zwischen
   * Messstellen- und Verbraucherliste behält die KI-Komponente – der neue Typ
   * geht als Vorgabe an sie.
   */
  typSetzen(datei: ImportDatei, typ: ProjektListenTyp): void {
    if (datei.listenTyp === typ) {
      return;
    }

    const warKi = this.importZiel(datei) === 'inbetriebnahme';

    datei.listenTyp = typ;
    datei.typManuellGesetzt = true;
    datei.vorschau = null;
    datei.vorschauSeite = 1;
    datei.kiVorbereitung = null;
    datei.bestaetigt = false;
    datei.fehler = null;
    datei.sheetName = istSpsListenTyp(typ) ? (datei.erkennung?.blatt ?? null) : null;

    const istKi = this.importZiel(datei) === 'inbetriebnahme';

    if (!istKi) {
      // Die KI-Komponente passt nicht mehr zum Typ – sie wird abgebaut.
      datei.kiAktiviert = false;
    } else if (!warKi && this.schritt === 2 && datei === this.aktuelleDatei) {
      datei.kiAktiviert = true;
    }

    this.entwurfSpeichern();

    if (this.schritt === 2 && datei === this.aktuelleDatei && istSpsListenTyp(typ)) {
      this.vorschauLaden(datei);
    }
  }

  importZiel(datei: ImportDatei): ImportZiel | null {
    return this.koordinator.importZielFuer(datei.listenTyp);
  }

  get bereitFuerVorschau(): boolean {
    return !this.importLaeuft
      && this.dateien.length > 0
      && this.dateien.every(d => !d.erkennungLaeuft && d.listenTyp !== 'Unbekannt' && !d.fehler);
  }

  get vorschauBlockiertGrund(): string | null {
    if (this.dateien.length === 0) {
      return 'Bitte mindestens eine Datei wählen.';
    }

    if (this.dateien.some(d => d.erkennungLaeuft)) {
      return 'Die Erkennung läuft noch.';
    }

    if (this.dateien.some(d => d.fehler)) {
      return 'Mindestens eine Datei konnte nicht gelesen werden – bitte entfernen.';
    }

    if (this.dateien.some(d => d.listenTyp === 'Unbekannt')) {
      return 'Bitte für jede Datei den Listentyp wählen.';
    }

    return null;
  }

  weiterZuVorschauen(): void {
    if (!this.bereitFuerVorschau) {
      this.fehler = this.vorschauBlockiertGrund ?? '';
      return;
    }

    this.fehler = '';
    this.schritt = 2;
    this.dateiAnzeigen(0);
  }

  zurDateiauswahl(): void {
    this.fehler = '';
    this.schritt = 1;
    this.entwurfSpeichern();
  }


  // =========================================================
  // SCHRITT 2 – VORSCHAUEN
  // =========================================================

  get aktuelleDatei(): ImportDatei | null {
    return this.dateien[this.aktuelleDateiIndex] ?? null;
  }

  get istLetzteDatei(): boolean {
    return this.aktuelleDateiIndex >= this.dateien.length - 1;
  }

  dateiAnzeigen(index: number): void {
    if (index < 0 || index >= this.dateien.length) {
      return;
    }

    this.aktuelleDateiIndex = index;
    this.fehler = '';

    if (this.schritt === 2) {
      this.aktuelleVorschauSicherstellen();
    }

    this.entwurfSpeichern();
  }

  naechsteDatei(): void {
    this.dateiAnzeigen(this.aktuelleDateiIndex + 1);
  }

  vorherigeDatei(): void {
    this.dateiAnzeigen(this.aktuelleDateiIndex - 1);
  }

  /** Die sichtbare Datei bekommt ihre Vorschau: SPS vom Server, KI über die eingebettete Komponente. */
  private aktuelleVorschauSicherstellen(): void {
    const datei = this.aktuelleDatei;

    if (!datei) {
      return;
    }

    const ziel = this.importZiel(datei);

    if (ziel === 'inbetriebnahme') {
      // Einmal erzeugt, bleibt die Komponente – die Analyse startet nur beim ersten Mal.
      datei.kiAktiviert = true;
      return;
    }

    if (ziel === 'sps' && !datei.vorschau && !datei.vorschauLaeuft) {
      this.vorschauLaden(datei);
    }
  }

  /** Listentyp für die eingebettete KI-Komponente – nur Messstellen/Verbraucher. */
  kiListenTyp(datei: ImportDatei): ListenTyp | null {
    return datei.listenTyp === 'Messstellenliste' || datei.listenTyp === 'Verbraucherliste'
      ? datei.listenTyp
      : null;
  }

  private kiKomponenteFuer(datei: ImportDatei): MessstellenImportComponent | undefined {
    return this.kiKomponenten?.find(k => k.importSchluessel === datei.id);
  }

  /** Von der KI-Komponente gemeldete, bestätigte Einstellungen. */
  kiVorbereitungUebernehmen(datei: ImportDatei, vorbereitung: VorbereiteterKiImport): void {
    datei.kiVorbereitung = vorbereitung;
    this.entwurfSpeichern();
  }

  /** Einstellungen in der KI-Vorschau geändert – die Bestätigung gilt nicht mehr. */
  kiVorbereitungUngueltig(datei: ImportDatei): void {
    datei.bestaetigt = false;
    this.entwurfSpeichern();
  }


  // ── SPS-Vorschau (seitenweise, speichert nichts) ──

  vorschauLaden(datei: ImportDatei, seite?: number): void {
    if (!istSpsListenTyp(datei.listenTyp)) {
      return;
    }

    if (seite != null) {
      datei.vorschauSeite = Math.max(1, seite);
    }

    // Jede Anfrage bekommt eine Nummer; nur die Antwort der jüngsten zählt.
    const anforderung = ++datei.vorschauAnforderung;

    datei.vorschauLaeuft = true;
    datei.fehler = null;

    const sub = this.spsImportApi.vorschau(datei.file, datei.listenTyp, {
      sheetName: datei.sheetName,
      projektId: this.projektId,
      seite: datei.vorschauSeite,
      seitengroesse: datei.vorschauSeitengroesse
    })
      .pipe(finalize(() => {
        if (anforderung === datei.vorschauAnforderung) {
          datei.vorschauLaeuft = false;
        }

        this.entwurfSpeichern();
      }))
      .subscribe({
        next: v => {
          if (anforderung !== datei.vorschauAnforderung || !this.dateien.includes(datei)) {
            return;   // veraltete Antwort nach schnellem Seitenwechsel oder entfernter Datei
          }

          datei.vorschau = v;
          datei.sheetName = v.blatt;
          datei.vorschauSeite = v.seite;
          datei.bestaetigt = datei.bestaetigt && v.typPasst;
        },
        error: err => {
          if (anforderung !== datei.vorschauAnforderung) {
            return;
          }

          datei.fehler = SpsPruefungApiService.fehlertext(err, 'Die Vorschau konnte nicht geladen werden.');
        }
      });

    this.laufendeAnfragen.add(sub);
  }

  get aktuelleVorschauZeilen(): SpsImportVorschauZeile[] {
    return this.aktuelleDatei?.vorschau?.vorschau ?? [];
  }

  vorschauSeiteWechseln(richtung: number): void {
    const datei = this.aktuelleDatei;

    if (!datei?.vorschau || datei.vorschauLaeuft) {
      return;
    }

    const neueSeite = datei.vorschauSeite + richtung;

    if (neueSeite < 1 || neueSeite > datei.vorschau.seitenGesamt) {
      return;
    }

    this.vorschauLaden(datei, neueSeite);
  }

  vorschauSeitengroesseAendern(event: Event): void {
    const datei = this.aktuelleDatei;

    if (!datei) {
      return;
    }

    const groesse = Number((event.target as HTMLSelectElement).value);

    if (!this.vorschauSeitengroessen.includes(groesse)) {
      return;
    }

    datei.vorschauSeitengroesse = groesse;
    this.vorschauLaden(datei, 1);
  }

  /** Blattwechsel in der SPS-Vorschau. */
  vorschauBlattWechseln(datei: ImportDatei, event: Event): void {
    const blatt = (event.target as HTMLSelectElement).value;

    if (!blatt || blatt === datei.sheetName) {
      return;
    }

    datei.sheetName = blatt;
    datei.bestaetigt = false;
    this.vorschauLaden(datei, 1);
  }

  spsVorschauBereit(datei: ImportDatei): boolean {
    return !!datei.vorschau
      && !datei.vorschauLaeuft
      && !datei.fehler
      && datei.vorschau.typPasst
      && datei.vorschau.gueltig > 0;
  }

  private spsVorschauHinweis(datei: ImportDatei): string {
    if (datei.vorschauLaeuft) {
      return 'Die Vorschau wird noch geladen.';
    }

    if (datei.fehler) {
      return datei.fehler;
    }

    if (!datei.vorschau) {
      return 'Die Vorschau ist noch nicht geladen.';
    }

    if (!datei.vorschau.typPasst) {
      return 'Die Datei passt nicht zum gewählten Listentyp – Typ oder Tabellenblatt ändern.';
    }

    if (datei.vorschau.gueltig === 0) {
      return 'Die Datei enthält keine importierbaren Signalzeilen.';
    }

    return '';
  }


  // ── Vorschau bestätigen ──

  /** Prüft die sichtbare Vorschau mit den bestehenden Regeln; speichert nichts. */
  aktuelleVorschauBestaetigen(): boolean {
    const datei = this.aktuelleDatei;

    if (!datei) {
      return false;
    }

    const ziel = this.importZiel(datei);

    if (ziel === 'inbetriebnahme') {
      const komponente = this.kiKomponenteFuer(datei);

      if (!komponente) {
        this.fehler = 'Die KI-Vorschau ist noch nicht geladen.';
        return false;
      }

      if (!komponente.importVorbereiten()) {
        this.fehler = komponente.uebernahmeHinweis ?? 'Die KI-Vorschau ist noch nicht vollständig.';
        return false;
      }
    } else if (ziel === 'sps') {
      if (!this.spsVorschauBereit(datei)) {
        this.fehler = this.spsVorschauHinweis(datei);
        return false;
      }
    } else {
      this.fehler = 'Für diese Datei ist kein Listentyp gewählt.';
      return false;
    }

    datei.bestaetigt = true;
    this.fehler = '';
    this.entwurfSpeichern();
    return true;
  }

  weiterZurBestaetigung(): void {
    if (!this.aktuelleVorschauBestaetigen()) {
      return;
    }

    if (!this.istLetzteDatei) {
      this.naechsteDatei();
      return;
    }

    const offen = this.dateien.find(d => !d.bestaetigt);

    if (offen) {
      this.dateiAnzeigen(this.dateien.indexOf(offen));
      this.fehler = `„${offen.file.name}“ ist noch nicht bestätigt.`;
      return;
    }

    this.schritt = 3;
    this.entwurfSpeichern();
  }

  zurLetztenVorschau(): void {
    if (this.importLaeuft) {
      return;
    }

    this.fehler = '';
    this.schritt = 2;
    this.dateiAnzeigen(Math.max(0, this.dateien.length - 1));
  }

  vorschauOeffnen(datei: ImportDatei): void {
    if (this.importLaeuft) {
      return;
    }

    this.schritt = 2;
    this.dateiAnzeigen(this.dateien.indexOf(datei));
  }


  // =========================================================
  // SCHRITT 3 – ZUSAMMENFASSUNG UND GEMEINSAMER IMPORT
  // =========================================================

  /** Zeilen, die eine Datei importieren würde – aus KI-Vorbereitung bzw. SPS-Vorschau. */
  zeilenZumImport(datei: ImportDatei): number | null {
    if (this.importZiel(datei) === 'inbetriebnahme') {
      return datei.kiVorbereitung?.zeilenImport ?? null;
    }

    return datei.vorschau?.gueltig ?? null;
  }

  ausgeschlosseneZeilen(datei: ImportDatei): number | null {
    if (this.importZiel(datei) === 'inbetriebnahme') {
      return datei.kiVorbereitung?.zeilenAusgeschlossen ?? null;
    }

    return datei.vorschau?.ungueltig ?? null;
  }

  warnungenVon(datei: ImportDatei): string[] {
    return this.importZiel(datei) === 'sps' ? (datei.vorschau?.warnungen ?? []) : [];
  }

  listenNameVon(datei: ImportDatei): string {
    if (this.importZiel(datei) === 'inbetriebnahme') {
      return datei.kiVorbereitung?.listenName || datei.file.name;
    }

    return datei.name || datei.file.name.replace(/\.(xlsx|xlsm|xls)$/i, '');
  }

  // ── Anlagenteile aus Ort bzw. Path ──

  /** Dateien, die eine bestimmte Anlagenteilspalte liefern können (Vorschau geladen, Spalte vorhanden). */
  dateienMitAnlagenteilSpalte(spalte: SpsAnlagenteilSpalte): ImportDatei[] {
    return this.dateien.filter(d => d.vorschau?.anlagenteilSpalteVerfuegbar === spalte);
  }

  /**
   * Die drei Optionen der Auswahl – eine FESTE Liste. Früher wurden die Optionen je
   * Change Detection neu erzeugt (Getter ohne trackBy); Angular baute die
   * mat-button-toggles dadurch bei jedem Durchlauf neu auf, und das Zerstören der
   * alten Toggles setzte die frische Auswahl der Gruppe wieder zurück.
   */
  readonly anlagenteilOptionen: { wert: SpsAnlagenteilSpalte | 'keine'; text: string }[] = [
    { wert: 'keine', text: 'Keine Anlagenteile' },
    { wert: 'Ort', text: anlagenteilSpalteText('Ort') },
    { wert: 'Path', text: anlagenteilSpalteText('Path') }
  ];

  /** Wert der Toggle-Gruppe: 'keine' statt null, damit die Gruppe die Option sicher wiederfindet. */
  get anlagenteilAuswahl(): SpsAnlagenteilSpalte | 'keine' {
    return this.anlagenteilQuelle ?? 'keine';
  }

  /** Quellen, für die mindestens eine passende Datei mit dieser Spalte vorliegt. */
  get anlagenteilQuellen(): { spalte: SpsAnlagenteilSpalte; text: string; dateien: number }[] {
    const quellen: { spalte: SpsAnlagenteilSpalte; text: string; dateien: number }[] = [];

    for (const spalte of ['Ort', 'Path'] as SpsAnlagenteilSpalte[]) {
      const anzahl = this.dateienMitAnlagenteilSpalte(spalte).length;

      if (anzahl > 0) {
        quellen.push({ spalte, text: anlagenteilSpalteText(spalte), dateien: anzahl });
      }
    }

    return quellen;
  }

  /** Gibt es im Import überhaupt SPS-Dateien, für die die Auswahl Sinn hat? */
  get anlagenteilAuswahlSichtbar(): boolean {
    return this.dateien.some(d => istSpsListenTyp(d.listenTyp));
  }

  anlagenteilQuelleVerfuegbar(wert: SpsAnlagenteilSpalte | 'keine'): boolean {
    return wert === 'keine' || this.dateienMitAnlagenteilSpalte(wert).length > 0;
  }

  /** Warum eine Quelle nicht wählbar ist – für den Hinweis unter der Auswahl. */
  anlagenteilQuelleHinweis(wert: SpsAnlagenteilSpalte | 'keine'): string | null {
    if (wert === 'keine' || this.anlagenteilQuelleVerfuegbar(wert)) {
      return null;
    }

    const typ: ProjektListenTyp = wert === 'Ort' ? 'SpsZuordnungsliste' : 'PlcTagListe';
    const dateien = this.dateien.filter(d => d.listenTyp === typ);

    if (dateien.length === 0) {
      return `${anlagenteilSpalteText(wert)}: keine ${listenTypInfo(typ).titel} in diesem Import.`;
    }

    if (dateien.some(d => !d.vorschau)) {
      return `${anlagenteilSpalteText(wert)}: die Vorschau der ${listenTypInfo(typ).titel} ist noch nicht geladen.`;
    }

    return `${anlagenteilSpalteText(wert)}: die Spalte „${wert}“ wurde in der Datei nicht gefunden.`;
  }

  get anlagenteilQuelleText(): string {
    return anlagenteilSpalteText(this.anlagenteilQuelle);
  }

  /** Auswahl aus der Toggle-Gruppe übernehmen; nicht verfügbare Quellen fallen auf „keine“ zurück. */
  anlagenteilQuelleSetzen(wert: SpsAnlagenteilSpalte | 'keine' | null | undefined): void {
    const spalte: SpsAnlagenteilSpalte | null = wert === 'Ort' || wert === 'Path' ? wert : null;
    this.anlagenteilQuelle = spalte && this.anlagenteilQuelleVerfuegbar(spalte) ? spalte : null;
    this.entwurfSpeichern();
  }

  /** Erkannte Anlagenteile der gewählten Quelle über alle passenden Dateien – je Wert einmal. */
  get anlagenteilVorschau(): { werte: SpsAnlagenteilWert[]; neu: number; vorhanden: number; eintraege: number } | null {
    const spalte = this.anlagenteilQuelle;

    if (!spalte) {
      return null;
    }

    const nachWert = new Map<string, SpsAnlagenteilWert>();
    let eintraege = 0;

    for (const d of this.dateienMitAnlagenteilSpalte(spalte)) {
      const v = d.vorschau?.anlagenteile;

      if (!v) {
        continue;
      }

      eintraege += v.eintraegeMitWert;

      for (const w of v.werte) {
        const schluessel = w.wert.trim().toLowerCase();
        const vorhanden = nachWert.get(schluessel);

        if (vorhanden) {
          vorhanden.anzahl += w.anzahl;
        } else {
          nachWert.set(schluessel, { ...w });
        }
      }
    }

    const werte = Array.from(nachWert.values()).sort((a, b) => a.wert.localeCompare(b.wert, 'de'));

    return {
      werte,
      neu: werte.filter(w => !w.vorhanden).length,
      vorhanden: werte.filter(w => w.vorhanden).length,
      eintraege
    };
  }

  /** Die Spalte, mit der eine Datei importiert wird – nur Dateien der gewählten Quelle. */
  anlagenteilSpalteFuer(datei: ImportDatei): SpsAnlagenteilSpalte | null {
    return this.anlagenteilQuelle && datei.vorschau?.anlagenteilSpalteVerfuegbar === this.anlagenteilQuelle
      ? this.anlagenteilQuelle
      : null;
  }

  trackByAnlagenteilWert(_index: number, w: SpsAnlagenteilWert): string {
    return w.wert;
  }

  trackByAnlagenteilOption(_index: number, o: { wert: SpsAnlagenteilSpalte | 'keine' }): string {
    return o.wert;
  }

  /** Dateien, die der nächste Klick auf „Import starten“ senden würde. */
  get zuImportierendeDateien(): ImportDatei[] {
    return this.dateien.filter(d => d.importStatus === 'bereit' || d.importStatus === 'fehlgeschlagen');
  }

  get erfolgreicheDateien(): ImportDatei[] {
    return this.dateien.filter(d => d.importStatus === 'erfolgreich');
  }

  get unklareDateien(): ImportDatei[] {
    return this.dateien.filter(d => d.importStatus === 'unklar');
  }

  get importAbgeschlossen(): boolean {
    return !this.importLaeuft && this.dateien.length > 0 && this.dateien.every(d => d.importStatus === 'erfolgreich');
  }

  get importGestartet(): boolean {
    return this.dateien.some(d => d.importStatus !== 'bereit');
  }

  get kannImportStarten(): boolean {
    return this.schritt === 3
      && !this.importLaeuft
      && this.zuImportierendeDateien.length > 0
      && this.zuImportierendeDateien.every(d => d.bestaetigt && this.importZiel(d) !== null);
  }

  get importBlockiertGrund(): string | null {
    if (this.importLaeuft) {
      return null;
    }

    if (this.dateien.length === 0) {
      return 'Keine Dateien ausgewählt.';
    }

    if (this.zuImportierendeDateien.length === 0) {
      return this.unklareDateien.length > 0
        ? 'Bei mindestens einer Datei ist unklar, ob sie gespeichert wurde. Bitte im Projekt prüfen und dann unten freigeben.'
        : null;
    }

    const unbestaetigt = this.zuImportierendeDateien.find(d => !d.bestaetigt);

    if (unbestaetigt) {
      return `„${unbestaetigt.file.name}“ ist noch nicht bestätigt.`;
    }

    return null;
  }

  /**
   * Der einzige Schritt, der speichert. Dateien werden nacheinander gesendet,
   * jede über den für ihren Typ bestehenden Import. Erfolgreiche Dateien
   * werden übersprungen, unklare nicht angefasst.
   */
  gesamtimportStarten(): void {
    if (!this.kannImportStarten) {
      this.fehler = this.importBlockiertGrund ?? '';
      return;
    }

    const dateien = this.zuImportierendeDateien;

    this.importLaeuft = true;
    this.fehler = '';

    from(dateien).pipe(
      concatMap(datei => this.dateiImportieren(datei)),
      finalize(() => {
        this.importLaeuft = false;
        this.importAktuelleDateiId = null;

        if (this.importAbgeschlossen) {
          this.entwurf.leeren();
        } else {
          this.entwurfSpeichern();
        }
      })
    ).subscribe({
      complete: () => {
        const fehlgeschlagen = this.dateien.filter(d => d.importStatus === 'fehlgeschlagen').length;
        const unklar = this.unklareDateien.length;

        if (fehlgeschlagen > 0 || unklar > 0) {
          this.fehler = `${this.erfolgreicheDateien.length} von ${this.dateien.length} Listen gespeichert`
            + (fehlgeschlagen > 0 ? `, ${fehlgeschlagen} fehlgeschlagen` : '')
            + (unklar > 0 ? `, ${unklar} ohne Antwort` : '') + '.';
        }
      }
    });
  }

  private dateiImportieren(datei: ImportDatei): Observable<void> {
    datei.importStatus = 'wirdImportiert';
    datei.importMeldung = null;
    this.importAktuelleDateiId = datei.id;

    const ziel = this.importZiel(datei);

    let anfrage: Observable<ImportDateiErgebnis>;

    if (ziel === 'inbetriebnahme') {
      // Frischer Stand aus der noch lebenden Komponente – falls der Benutzer
      // nach der Bestätigung noch etwas geändert hat.
      const komponente = this.kiKomponenteFuer(datei);

      if (komponente && !komponente.importVorbereiten()) {
        // Die Komponente lebt noch, ist aber nicht mehr importbereit (z. B. Vorschau
        // wird gerade neu gerechnet) – nicht mit einem alten Stand speichern.
        datei.importStatus = 'fehlgeschlagen';
        datei.importMeldung = komponente.uebernahmeHinweis ?? 'Die KI-Vorschau ist nicht importbereit.';
        datei.bestaetigt = false;
        return of(void 0);
      }

      const v = datei.kiVorbereitung;

      if (!v) {
        datei.importStatus = 'fehlgeschlagen';
        datei.importMeldung = 'Die KI-Vorschau wurde nicht bestätigt.';
        datei.bestaetigt = false;
        return of(void 0);
      }

      anfrage = this.ibnService.import(
        v.file, v.sheetName, v.headerRowNumber, v.listenName, v.customerId,
        v.projektId ?? this.projektId, v.analysis, v.regeln, v.entscheidungen
      ).pipe(map((liste: MessstellenListeDetail) => ({
        listeId: liste.id,
        listeName: liste.name,
        eintraege: liste.messstellenCount,
        warnungen: []
      })));
    } else if (ziel === 'sps') {
      anfrage = this.spsImportApi.importieren(datei.file, this.projektId, datei.listenTyp, {
        sheetName: datei.sheetName,
        name: datei.name,
        anlagenteilSpalte: this.anlagenteilSpalteFuer(datei)
      }).pipe(map((e: SpsImportErgebnis) => ({
        listeId: e.liste.id,
        listeName: e.liste.name,
        eintraege: e.eintraegeGespeichert,
        ungeklaert: e.signaleUngeklaert,
        zusammengefuehrt: e.automatischZusammengefuehrt,
        warnungen: e.warnungen
      })));
    } else {
      datei.importStatus = 'fehlgeschlagen';
      datei.importMeldung = 'Kein Listentyp gewählt.';
      return of(void 0);
    }

    return anfrage.pipe(
      map(ergebnis => {
        datei.importStatus = 'erfolgreich';
        datei.importErgebnis = ergebnis;
        datei.importMeldung = `${ergebnis.eintraege} Einträge als „${ergebnis.listeName}“ gespeichert`
          + (ergebnis.zusammengefuehrt ? `, ${ergebnis.zusammengefuehrt} Signal(e) automatisch zusammengeführt` : '')
          + (ergebnis.ungeklaert ? `, ${ergebnis.ungeklaert} Signalzuordnung(en) zu klären` : '') + '.';
      }),
      catchError(err => {
        datei.importStatus = this.importFehlerStatus(err);
        datei.importMeldung = datei.importStatus === 'unklar'
          ? 'Keine Antwort vom Server (Verbindung unterbrochen oder Zeitüberschreitung). Die Liste kann trotzdem gespeichert worden sein – bitte im Projekt nachsehen, bevor du es erneut versuchst.'
          : SpsPruefungApiService.fehlertext(err, 'Der Import ist fehlgeschlagen.');
        return of(void 0);
      }),
      finalize(() => this.entwurfSpeichern())
    );
  }

  /**
   * Ohne Antwort weiß niemand, ob der Server gespeichert hat. Ein 4xx/5xx MIT
   * Antwort heißt dagegen: nichts gespeichert, erneut senden ist sicher.
   */
  private importFehlerStatus(err: unknown): ImportDateiStatus {
    if (err instanceof TimeoutError) {
      return 'unklar';
    }

    if (err instanceof HttpErrorResponse) {
      return err.status === 0 || err.status === 408 || err.status === 502 || err.status === 503 || err.status === 504
        ? 'unklar'
        : 'fehlgeschlagen';
    }

    return 'fehlgeschlagen';
  }

  /** Der Benutzer hat im Projekt geprüft, dass die Liste NICHT angelegt wurde – erneuter Versuch freigegeben. */
  unklarFreigeben(datei: ImportDatei): void {
    if (datei.importStatus !== 'unklar' || this.importLaeuft) {
      return;
    }

    datei.importStatus = 'fehlgeschlagen';
    datei.importMeldung = 'Zum erneuten Versuch freigegeben.';
    this.fehler = '';
    this.entwurfSpeichern();
  }

  /** Der Benutzer hat im Projekt gesehen, dass die Liste bereits angelegt wurde. */
  unklarAlsGespeichertMarkieren(datei: ImportDatei): void {
    if (datei.importStatus !== 'unklar' || this.importLaeuft) {
      return;
    }

    datei.importStatus = 'erfolgreich';
    datei.importMeldung = 'Vom Benutzer als bereits gespeichert bestätigt.';
    this.fehler = '';

    if (this.importAbgeschlossen) {
      this.entwurf.leeren();
    } else {
      this.entwurfSpeichern();
    }
  }

  statusText(status: ImportDateiStatus): string {
    switch (status) {
      case 'wirdImportiert': return 'Wird importiert …';
      case 'erfolgreich': return 'Erfolgreich';
      case 'fehlgeschlagen': return 'Fehlgeschlagen';
      case 'unklar': return 'Ergebnis unklar';
      default: return 'Bereit';
    }
  }

  ergebnisOeffnen(datei: ImportDatei): void {
    const e = datei.importErgebnis;

    if (!e) {
      return;
    }

    if (this.importZiel(datei) === 'sps') {
      void this.router.navigate(['/projekte', this.projektId, 'sps-listen', e.listeId]);
    } else {
      void this.router.navigate(['/wartung/inbetriebnahme/liste', e.listeId], { queryParams: { vonProjekt: this.projektId } });
    }
  }


  // =========================================================
  // ÜBERSICHT, ENTWURF, NAVIGATION, HILFEN
  // =========================================================

  anzahl(typ: ProjektListenTyp): number {
    return this.dateien.filter(d => d.listenTyp === typ).length;
  }

  typTitel(typ: ProjektListenTyp): string {
    return typ === 'Unbekannt' ? 'Unbekannt' : listenTypInfo(typ).titel;
  }

  typIcon(typ: ProjektListenTyp): string {
    return typ === 'Unbekannt' ? 'help_outline' : listenTypInfo(typ).icon;
  }

  dateigroesse(bytes: number): string {
    return bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
      : `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
  }

  trackByDatei(_index: number, datei: ImportDatei): string {
    return datei.id;
  }

  private entwurfSpeichern(): void {
    this.entwurf.speichern({
      projektId: this.projektId,
      projektName: this.projektName,
      dateien: this.dateien,
      aktuelleDateiIndex: this.aktuelleDateiIndex,
      schritt: this.schritt,
      anlagenteilQuelle: this.anlagenteilQuelle
    });
  }

  private entwurfWiederherstellen(): void {
    const entwurf = this.entwurf.laden(this.projektId);

    if (!entwurf || entwurf.dateien.length === 0) {
      return;
    }

    this.dateien = entwurf.dateien;
    this.aktuelleDateiIndex = Math.min(entwurf.aktuelleDateiIndex, Math.max(0, this.dateien.length - 1));
    this.schritt = entwurf.schritt ?? 1;
    this.anlagenteilQuelle = entwurf.anlagenteilQuelle ?? null;

    if (!this.projektName) {
      this.projektName = entwurf.projektName;
    }

    for (const d of this.dateien) {
      // Requests der alten Instanz sind weg; alles Unfertige wird neu angestoßen.
      d.vorschauLaeuft = false;

      if (d.erkennungLaeuft || (!d.erkennung && !d.fehler)) {
        this.erkennungStarten(d);
      }

      // Die KI-Komponenten der alten Instanz sind zerstört – ihre Einstellungen
      // leben nur noch in der bestätigten Vorbereitung. Die Komponente wird beim
      // nächsten Anzeigen neu aufgebaut (erneute Analyse); die Bestätigung bleibt.
      d.kiAktiviert = false;

      if (d.importStatus === 'wirdImportiert') {
        d.importStatus = 'unklar';
        d.importMeldung = 'Der Import wurde unterbrochen, bevor eine Antwort kam. Bitte im Projekt prüfen.';
      }
    }

    if (this.schritt === 2) {
      this.aktuelleVorschauSicherstellen();
    }
  }

  zurueckZumProjekt(): void {
    void this.router.navigate(['/projekte', this.projektId]);
  }

  zurSpsPruefung(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-pruefung']);
  }

  neuenImportBeginnen(): void {
    if (this.importLaeuft) {
      return;
    }

    this.dateien = [];
    this.aktuelleDateiIndex = 0;
    this.schritt = 1;
    this.fehler = '';
    this.anlagenteilQuelle = null;
    this.entwurf.leeren();
  }
}
